/**
 * Pure computations for studio-wide capture stats and quota counts.
 * Shared by StudioMetricsProvider and StudioStatsBar display.
 */

import { ROLLING_CAPTURE_PERIOD_MS } from '../../constants/uploadLimits';
import { readCaptureFrameFields } from '../../utils/uploadProgress';

export function computeStudioDisplayStats(captures) {
  let totalSplats = 0;
  let totalMB = 0;
  let totalFrames = 0;
  for (const c of captures) {
    totalSplats += Number(c.processing?.splatCount ?? 0) || 0;
    totalMB += Number(c.processing?.splatSizeMB ?? 0) || 0;
    const extracted = readCaptureFrameFields(c.processing || {}).framesExtracted;
    totalFrames += Number(extracted ?? 0) || 0;
  }
  return {
    totalSplats,
    totalMB,
    totalFrames,
    totalCaptureCount: captures.length,
  };
}

const PROCESSING_STATUSES = new Set(['processing', 'queued']);

export function computeStudioQuotaCounts(captures, options = {}) {
  const periodMs = options.periodMs ?? ROLLING_CAPTURE_PERIOD_MS;
  const planStartedAtMs = options.planStartedAtMs ?? null;
  const windowStart = Date.now() - periodMs;
  const cutoffMs =
    planStartedAtMs != null && Number.isFinite(Number(planStartedAtMs))
      ? Math.max(Number(planStartedAtMs), windowStart)
      : windowStart;

  let processingCount = 0;
  let completedCount = 0;
  let rollingCompletedCount = 0;

  for (const c of captures) {
    const st = c.processing?.status;
    if (PROCESSING_STATUSES.has(st)) processingCount += 1;
    if (st === 'completed') {
      completedCount += 1;
      const created = c.createdAt;
      const t = created?.toMillis?.() ?? 0;
      if (t >= cutoffMs) rollingCompletedCount += 1;
    }
  }

  return { processingCount, completedCount, rollingCompletedCount };
}

/** Callable / Firestore may return int64 as string; normalize to integer bytes. */
export function coerceStorageBytes(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.floor(value));
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
    const n = Number(value.trim());
    if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
  }
  return null;
}

/** Merge metrics doc with live Firestore scan — never show fewer completions than exist in studio data. */
export function mergeFreeLifetimeCaptureTotals(metricsTotal, studioVerified) {
  if (metricsTotal == null && studioVerified == null) return null;
  if (metricsTotal != null && studioVerified == null) return metricsTotal;
  if (metricsTotal == null && studioVerified != null) return studioVerified;
  return Math.max(metricsTotal, studioVerified);
}
